// src/pages/Landing.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '../store/uiStore';
import Footer from '../components/layout/Footer';
import { C } from '../styles/tokens';

/* ── Orbit Graphic — hidden on mobile ─────── */
function OrbitGfx() {
  const nodes = [
    { label: '🔐', text: 'KYC',   anim: 'orbit',  color: C.magenta, dur: '7s'  },
    { label: '🪙', text: 'Token', anim: 'orbit2', color: C.warning, dur: '11s' },
    { label: '🤖', text: 'AI',    anim: 'orbit3', color: C.violet,  dur: '9s'  },
  ];
  return (
    <div style={{ position: 'relative', width: 340, height: 340, flexShrink: 0 }}>
      {[100, 138, 162].map((r, i) => (
        <div key={i} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: r * 2, height: r * 2, borderRadius: '50%', border: `1px solid rgba(254,52,232,${0.22 - i * 0.06})` }} />
      ))}
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 80, height: 80, borderRadius: '50%', background: `radial-gradient(circle,${C.magenta}60,${C.violet}40,transparent)`, boxShadow: `0 0 60px ${C.magenta}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, animation: 'glow 3s ease-in-out infinite' }}>⬡</div>
      {nodes.map((n, i) => (
        <div key={i} style={{ position: 'absolute', top: '50%', left: '50%', width: 50, height: 50, marginTop: -25, marginLeft: -25, borderRadius: 12, background: 'rgba(255,255,255,.06)', border: `1px solid ${n.color}40`, backdropFilter: 'blur(8px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, animation: `${n.anim} ${n.dur} linear infinite` }}>
          <span style={{ fontSize: 17 }}>{n.label}</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: n.color }}>{n.text}</span>
        </div>
      ))}
      {[
        { icon: '💼', label: 'Jobs',      x: '80%', y: '12%', delay: '0s'   },
        { icon: '📜', label: 'Certs',     x: '2%',  y: '16%', delay: '1s'   },
        { icon: '🌍', label: 'Global',    x: '2%',  y: '70%', delay: '2s'   },
        { icon: '📊', label: 'Analytics', x: '78%', y: '72%', delay: '1.5s' },
      ].map((b, i) => (
        <div key={i} style={{ position: 'absolute', left: b.x, top: b.y, padding: '5px 9px', borderRadius: 8, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(254,52,232,.2)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', gap: 5, animation: 'float 4s ease-in-out infinite', animationDelay: b.delay }}>
          <span style={{ fontSize: 12 }}>{b.icon}</span>
          <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.7)' }}>{b.label}</span>
        </div>
      ))}
    </div>
  );
}

const FEATURES = [
  { icon: '🔐', title: 'KYC Verification',   color: C.magenta,  tags: ['Facial AI', 'Anti-Spoofing'],  desc: 'Liveness detection, government ID scan, and fraud-resistant onboarding in minutes.' },
  { icon: '🤖', title: 'AI Career Engine',    color: C.violet,   tags: ['CV Parser', 'ATS Score'],      desc: 'Neural CV parsing, ATS scoring, keyword optimization, and smart opportunity matching.' },
  { icon: '🪙', title: 'ONT Token Wallet',    color: C.warning,  tags: ['Solana', 'USDT Bridge'],       desc: 'Solana-powered wallet auto-generated on signup. Send, receive, convert, withdraw.' },
  { icon: '📜', title: 'Certified Training',  color: C.success,  tags: ['LMS', 'Digital Badges'],       desc: 'Accredited courses, proctored exams, blockchain-verified digital certificates.' },
  { icon: '🌐', title: 'Global Job Board',    color: C.info,     tags: ['Real-Time', 'AI Matched'],     desc: 'Elasticsearch-powered search across thousands of vetted global opportunities.' },
  { icon: '🏢', title: 'Collaborator Portal', color: C.lavender, tags: ['B2B Portal', 'AI Matching'],   desc: 'Company dashboards, applicant tracking, AI match scores, and token payments.' },
];

const STATS = [
  { v: '50K+',   l: 'Verified Talents', i: '👥' },
  { v: '2,400+', l: 'Opportunities',    i: '💼' },
  { v: '$4.2M',  l: 'ONT Distributed',  i: '🪙' },
  { v: '94%',    l: 'Placement Rate',   i: '🎯' },
  { v: '180+',   l: 'Countries',        i: '🌍' },
  { v: '1,200+', l: 'Collaborators',    i: '🤝' },
];

const FLOW_TABS = {
  Talent: [
    { n: '01', i: '📧', t: 'Email & OTP',    d: 'Enter email, receive OTP, verify instantly.' },
    { n: '02', i: '📱', t: 'Phone Verify',   d: 'SMS OTP — dual-layer security from day one.' },
    { n: '03', i: '🪪', t: 'KYC Check',      d: 'Upload government ID + AI liveness detection.' },
    { n: '04', i: '🪙', t: 'Wallet Created', d: 'ONT Wallet auto-generated on Solana blockchain.' },
    { n: '05', i: '🤖', t: 'AI Profile',     d: 'Upload CV — AI parses, scores, and optimizes it.' },
    { n: '06', i: '🎯', t: 'Apply & Earn',   d: 'Apply to roles, track stages, get paid in ONT.' },
  ],
  Collaborator: [
    { n: '01', i: '🏢', t: 'Register Company',  d: 'Submit business details and verification documents.' },
    { n: '02', i: '✅', t: 'Admin Approval',     d: 'Reviewed and approved within 24–72 hours.' },
    { n: '03', i: '📋', t: 'Post Opportunity',   d: 'Create listings — admin-moderated for quality.' },
    { n: '04', i: '🧠', t: 'AI-Ranked Talent',   d: 'Review candidates ranked by AI match score.' },
    { n: '05', i: '🤝', t: 'Select Candidate',   d: 'Shortlist, communicate, and make your selection.' },
    { n: '06', i: '🪙', t: 'Pay via ONT',        d: 'Instant blockchain payment to candidate wallet.' },
  ],
};

export default function Landing() {
  const navigate = useNavigate();
  const { openAuthModal } = useUIStore();
  const [tab, setTab] = useState('Talent');

  return (
    <div style={{ background: C.bg }}>
      <style>{`
        /* ── Landing mobile breakpoints ── */
        .lnd-hero-row {
          display: flex;
          align-items: center;
          gap: 60px;
          justify-content: space-between;
        }
        .lnd-orbit { display: block; }
        .lnd-stats-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
        }
        .lnd-features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
        }
        .lnd-flow-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .lnd-cta-btns {
          display: flex;
          gap: 14px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .lnd-hero-badges {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }
        .lnd-hero-btns {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 36px;
        }

        /* Tablet */
        @media (max-width: 900px) {
          .lnd-stats-grid  { grid-template-columns: repeat(3, 1fr) !important; }
          .lnd-features-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .lnd-flow-grid   { grid-template-columns: repeat(2, 1fr) !important; }
          .lnd-hero-row    { gap: 32px !important; }
          .lnd-orbit       { display: none !important; }
        }

        /* Mobile */
        @media (max-width: 600px) {
          .lnd-hero-row    { flex-direction: column !important; align-items: flex-start !important; gap: 0 !important; }
          .lnd-orbit       { display: none !important; }
          .lnd-stats-grid  { grid-template-columns: repeat(2, 1fr) !important; }
          .lnd-features-grid { grid-template-columns: 1fr !important; }
          .lnd-flow-grid   { grid-template-columns: 1fr !important; }
          .lnd-hero-btns   { flex-direction: column !important; }
          .lnd-hero-btns button { width: 100% !important; text-align: center !important; }
          .lnd-hero-badges { gap: 10px !important; }
          .lnd-hero-badges span { font-size: 11px !important; }
          .lnd-cta-btns    { flex-direction: column !important; align-items: stretch !important; padding: 0 8px !important; }
          .lnd-cta-btns button { width: 100% !important; }
          .lnd-flow-tabs   { flex-wrap: wrap !important; }
          .lnd-stat-border { border-right: none !important; border-bottom: 1px solid rgba(254,52,232,.08) !important; }
        }

        /* Prevent any horizontal overflow */
        .lnd-section { overflow-x: hidden !important; }
      `}</style>

      {/* ── HERO ─────────────────────────────── */}
      <section className="lnd-section" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden', paddingTop: 64 }}>
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse 80% 60% at 65% 40%,${C.navy}70 0%,transparent 60%),radial-gradient(ellipse 50% 40% at 15% 70%,${C.violet}25 0%,transparent 55%),radial-gradient(ellipse 35% 25% at 85% 8%,${C.magenta}18 0%,transparent 50%)` }} />
        <div className="grid-bg" />
        <div style={{ position: 'absolute', left: 0, right: 0, height: 1, background: `linear-gradient(90deg,transparent,${C.magenta}50,transparent)`, animation: 'scanLine 7s ease-in-out infinite', pointerEvents: 'none' }} />

        <div className="container" style={{ position: 'relative', zIndex: 2, width: '100%', padding: '40px clamp(16px,4vw,28px)' }}>
          <div className="lnd-hero-row">
            {/* Text side */}
            <div style={{ maxWidth: 580, flex: 1, minWidth: 0 }}>
              <div className="fu" style={{ marginBottom: 20 }}>
                <span className="pill" style={{ background: 'rgba(254,52,232,.12)', border: '1px solid rgba(254,52,232,.3)', color: C.magenta }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.magenta, animation: 'pulse 1.5s infinite', display: 'inline-block' }} />
                  {' '}Now Live — Web3 Talent Platform
                </span>
              </div>

              <h1 className="syne fu d1" style={{ fontSize: 'clamp(2rem,6vw,4.4rem)', fontWeight: 800, lineHeight: 1.08, marginBottom: 18, letterSpacing: '-0.03em', wordBreak: 'break-word' }}>
                The Future of<br />
                <span className="grad-text">Talent & Opportunity</span><br />
                Starts Here
              </h1>

              <p className="fu d2" style={{ fontSize: 'clamp(13px,2vw,16px)', color: 'rgba(255,255,255,.58)', lineHeight: 1.8, marginBottom: 28 }}>
                Oracle Nexus connects verified global talent with top opportunities through AI-powered matching, Solana blockchain payments, and the ONT Token ecosystem.
              </p>

              <div className="fu d3 lnd-hero-btns">
                <button onClick={() => openAuthModal('register')} style={{ padding: 'clamp(11px,2vw,14px) clamp(20px,3vw,30px)', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: 9, color: '#fff', fontSize: 'clamp(13px,2vw,15px)', fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif", transition: 'all .3s' }}>
                  Register as Talent →
                </button>
                <button onClick={() => navigate('/collaborators')} style={{ padding: 'clamp(10px,2vw,13px) clamp(18px,3vw,28px)', background: 'transparent', border: `1px solid rgba(254,52,232,.35)`, borderRadius: 9, color: '#fff', fontSize: 'clamp(13px,2vw,15px)', fontWeight: 600, cursor: 'pointer', transition: 'all .3s' }}>
                  Partner with Us
                </button>
              </div>

              <div className="fu d4 lnd-hero-badges">
                {['🔐 KYC Verified', '⛓ Solana', '🤖 AI-Powered', '📜 Certified'].map(b => (
                  <span key={b} style={{ fontSize: 'clamp(11px,1.5vw,12px)', color: 'rgba(255,255,255,.38)' }}>{b}</span>
                ))}
              </div>
            </div>

            {/* Orbit graphic — hidden on mobile via CSS */}
            <div className="lnd-orbit fu d5"><OrbitGfx /></div>
          </div>
        </div>

        <div style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,.25)', letterSpacing: '.12em', textTransform: 'uppercase' }}>Scroll</span>
          <div style={{ width: 1, height: 36, background: `linear-gradient(to bottom,${C.magenta}70,transparent)`, animation: 'pulse 2s ease-in-out infinite' }} />
        </div>
      </section>

      {/* ── STATS ────────────────────────────── */}
      <section className="lnd-section" style={{ padding: 'clamp(28px,4vw,44px) 0', background: `linear-gradient(90deg,${C.navy}30,${C.violet}20,${C.navy}30)`, borderTop: '1px solid rgba(254,52,232,.1)', borderBottom: '1px solid rgba(254,52,232,.1)' }}>
        <div className="container" style={{ padding: '0 clamp(16px,4vw,28px)' }}>
          <div className="lnd-stats-grid">
            {STATS.map((s, i) => (
              <div key={i} className={`fu lnd-stat-border`} style={{ padding: 'clamp(12px,2vw,18px) clamp(8px,2vw,12px)', textAlign: 'center', borderRight: i < 5 ? '1px solid rgba(254,52,232,.08)' : 'none', animationDelay: `${i * .08}s` }}>
                <div style={{ fontSize: 'clamp(18px,3vw,22px)', marginBottom: 5 }}>{s.i}</div>
                <div className="syne grad-text" style={{ fontSize: 'clamp(1.3rem,3vw,2rem)', fontWeight: 800, lineHeight: 1 }}>{s.v}</div>
                <div style={{ fontSize: 'clamp(10px,1.5vw,11px)', color: 'rgba(255,255,255,.38)', marginTop: 4 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────── */}
      <section className="lnd-section section" style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="grid-bg" style={{ opacity: .4 }} />
        <div className="container" style={{ position: 'relative', zIndex: 1, padding: '0 clamp(16px,4vw,28px)' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(36px,5vw,60px)' }}>
            <span className="pill" style={{ background: 'rgba(95,8,231,.15)', border: '1px solid rgba(95,8,231,.4)', color: C.lavender, marginBottom: 16, display: 'inline-flex' }}>Platform Capabilities</span>
            <h2 className="syne" style={{ fontSize: 'clamp(1.6rem,4vw,3rem)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 10 }}>
              Six Systems. <span className="grad-text">One Platform.</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,.45)', marginTop: 12, fontSize: 'clamp(13px,2vw,15px)', maxWidth: 500, margin: '12px auto 0' }}>
              From identity to income — every tool you need to own your career.
            </p>
          </div>

          <div className="lnd-features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="card fu" style={{ padding: 'clamp(20px,3vw,28px) clamp(16px,3vw,24px)', animationDelay: `${i * .08}s`, transition: 'all .3s' }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.borderColor=`rgba(254,52,232,.3)`; e.currentTarget.style.boxShadow=`0 20px 60px rgba(95,8,231,.25)`; }}
                onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.borderColor='rgba(254,52,232,.1)'; e.currentTarget.style.boxShadow='none'; }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${f.color}70,transparent)` }} />
                <div style={{ width: 46, height: 46, borderRadius: 12, background: `${f.color}15`, border: `1px solid ${f.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 16 }}>{f.icon}</div>
                <h3 className="syne" style={{ fontSize: 'clamp(14px,2vw,17px)', fontWeight: 700, marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: 'clamp(12px,1.5vw,13.5px)', color: 'rgba(255,255,255,.48)', lineHeight: 1.72, marginBottom: 14 }}>{f.desc}</p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {f.tags.map(t => <span key={t} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 6, background: `${f.color}10`, border: `1px solid ${f.color}25`, color: f.color }}>{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────── */}
      <section className="lnd-section section" style={{ background: C.surface1 }}>
        <div className="container" style={{ padding: '0 clamp(16px,4vw,28px)' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(28px,4vw,48px)' }}>
            <span className="pill" style={{ background: 'rgba(254,52,232,.1)', border: '1px solid rgba(254,52,232,.25)', color: C.magenta, marginBottom: 14, display: 'inline-flex' }}>User Flows</span>
            <h2 className="syne" style={{ fontSize: 'clamp(1.6rem,4vw,2.8rem)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 10 }}>
              How Oracle Nexus <span className="grad-text">Works</span>
            </h2>
          </div>

          <div className="lnd-flow-tabs" style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 'clamp(28px,4vw,44px)' }}>
            {Object.keys(FLOW_TABS).map(k => (
              <button key={k} onClick={() => setTab(k)} style={{ padding: 'clamp(8px,1.5vw,10px) clamp(18px,3vw,26px)', borderRadius: 100, background: tab === k ? `linear-gradient(135deg,${C.magenta},${C.violet})` : 'rgba(255,255,255,.05)', border: tab === k ? 'none' : '1px solid rgba(255,255,255,.1)', color: '#fff', fontSize: 'clamp(12px,2vw,14px)', fontWeight: 700, cursor: 'pointer', transition: 'all .3s', fontFamily: "'Syne',sans-serif", boxShadow: tab === k ? `0 8px 24px rgba(254,52,232,.3)` : 'none' }}>{k}</button>
            ))}
          </div>

          <div className="lnd-flow-grid">
            {FLOW_TABS[tab].map((step, i) => (
              <div key={i} className="glass fu" style={{ padding: 'clamp(18px,3vw,24px) clamp(14px,3vw,20px)', borderRadius: 14, animationDelay: `${i * .07}s`, transition: 'all .3s', position: 'relative' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(254,52,232,.28)'; e.currentTarget.style.transform='translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(254,52,232,.12)'; e.currentTarget.style.transform='translateY(0)'; }}>
                <div style={{ fontSize: 26, fontFamily: "'Syne',sans-serif", fontWeight: 900, color: 'rgba(254,52,232,.06)', position: 'absolute', top: 12, right: 14 }}>{step.n}</div>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(254,52,232,.1)', border: '1px solid rgba(254,52,232,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, marginBottom: 12 }}>{step.i}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                  <span style={{ fontSize: 10, color: C.magenta, fontWeight: 800, fontFamily: "'Syne',sans-serif" }}>{step.n}</span>
                  <h4 className="syne" style={{ fontSize: 'clamp(13px,1.8vw,15px)', fontWeight: 700 }}>{step.t}</h4>
                </div>
                <p style={{ fontSize: 'clamp(12px,1.5vw,13px)', color: 'rgba(255,255,255,.48)', lineHeight: 1.65 }}>{step.d}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 'clamp(24px,4vw,40px)' }}>
            <button onClick={() => openAuthModal('register')} style={{ padding: 'clamp(11px,2vw,14px) clamp(22px,4vw,32px)', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: 9, color: '#fff', fontSize: 'clamp(13px,2vw,15px)', fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif" }}>
              Start Your Journey →
            </button>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ───────────────────────── */}
      <section className="lnd-section section">
        <div className="container" style={{ padding: '0 clamp(16px,4vw,28px)' }}>
          <div style={{ background: `linear-gradient(135deg,${C.navy}80,${C.violet}40,${C.navy}80)`, border: '1px solid rgba(254,52,232,.22)', borderRadius: 'clamp(14px,3vw,24px)', padding: 'clamp(32px,6vw,64px) clamp(20px,5vw,48px)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -60, left: -60, width: 260, height: 260, borderRadius: '50%', background: `${C.magenta}12`, filter: 'blur(50px)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: `${C.violet}12`, filter: 'blur(50px)', pointerEvents: 'none' }} />

            <span className="pill" style={{ background: 'rgba(254,52,232,.12)', border: '1px solid rgba(254,52,232,.3)', color: C.magenta, marginBottom: 20, display: 'inline-flex', position: 'relative' }}>🚀 Join the Movement</span>
            <h2 className="syne" style={{ fontSize: 'clamp(1.5rem,4vw,2.8rem)', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 12, position: 'relative', lineHeight: 1.2 }}>
              Ready to Transform Your<br />
              <span className="grad-text">Career or Business?</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,.5)', fontSize: 'clamp(13px,2vw,15px)', marginBottom: 28, position: 'relative', maxWidth: 480, margin: '0 auto 28px' }}>
              Join 50,000+ verified professionals and 1,200+ collaborators already on Oracle Nexus.
            </p>
            <div className="lnd-cta-btns">
              <button onClick={() => openAuthModal('register')} style={{ padding: 'clamp(11px,2vw,14px) clamp(22px,4vw,32px)', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: 9, color: '#fff', fontSize: 'clamp(13px,2vw,15px)', fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif" }}>Register Free →</button>
              <button onClick={() => navigate('/collaborators')} style={{ padding: 'clamp(10px,2vw,13px) clamp(20px,3vw,30px)', background: 'transparent', border: `1px solid rgba(254,52,232,.35)`, borderRadius: 9, color: '#fff', fontSize: 'clamp(13px,2vw,15px)', fontWeight: 600, cursor: 'pointer' }}>Partner with Us</button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
